import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Dialog } from "@mui/material";
import {
  ArrowLeft, Video, Copy, Edit2, XCircle, Check, X, Calendar, Clock, User, Users2,
  FileText, ListChecks, Mail, MessageCircle, Languages, Hash, Info, History, ChevronDown, ChevronUp, Repeat,
  Sparkles, CheckCircle2, CircleDashed, AlertCircle,
} from "lucide-react";
import styles from "./hiralityMeetings.module.scss";
import MeetingForm from "./MeetingForm";
import EngagementHeatmap from "./EngagementHeatmap";
import MeetingChapters from "./MeetingChapters";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";
import { apiBaseUrl } from "../../../../../../services/config";
import { AVATAR_COLORS, STATUS_BADGE, authHeader, buildMeetingLink } from "./Meetings";
import { getMeetingType, SUMMARY_SUBTITLE } from "./meetingTypes";

const API = apiBaseUrl;

// A dashboard-style card — same visual language as the Meetings list page,
// with room for a subtitle explaining what each AI section actually is
// (the popup version this replaces had no space for that).
function SectionCard({ icon: Icon, iconColor, title, subtitle, headerRight, children, T }) {
  return (
    <div className={styles.card} style={{ marginBottom: 18 }}>
      <div className={styles.cardHeader}>
        <div>
          <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon size={15} style={{ color: iconColor }} /> {title}
          </h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {headerRight}
      </div>
      <div className={styles.cardBody}>{children}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, T }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: `${T.cyan}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={15} style={{ color: T.cyan }} />
      </div>
      <div>
        <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 13.5, color: T.text, fontWeight: 700 }}>{value}</div>
      </div>
    </div>
  );
}

export default function MeetingDetail() {
  const { T } = useTheme();
  const { mid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkChapter = searchParams.get("chapter") !== null ? Number(searchParams.get("chapter")) : null;

  const [meeting,  setMeeting]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [languages,   setLanguages]   = useState([]);
  const [summaryLang, setSummaryLang] = useState("en");
  const [translating, setTranslating] = useState(false);
  const [copiedId,    setCopiedId]    = useState(null);
  const [copiedDraft, setCopiedDraft] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  // Date-wise history of sibling occurrences — only populated (and only
  // rendered) when this meeting belongs to a recurring series.
  const [seriesHistory, setSeriesHistory] = useState({ is_recurring: false, occurrences: [] });
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [expandedOccurrence, setExpandedOccurrence] = useState(null);

  // Pre-meeting AI briefing — only meaningful for a not-yet-started meeting.
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  const fetchMeeting = async () => {
    try {
      const r = await axios.get(`${API}/hirality-meetings/${mid}`, { headers: authHeader() });
      setMeeting(r.data);
    } catch (e) {
      console.error("Failed to load meeting:", e);
      setNotFound(true);
    } finally { setLoading(false); }
  };

  const fetchSeriesHistory = async () => {
    setSeriesLoading(true);
    try {
      const r = await axios.get(`${API}/hirality-meetings/${mid}/series-history`, { headers: authHeader() });
      setSeriesHistory(r.data);
      setExpandedOccurrence(r.data.occurrences?.[0]?.id ?? null);
    } catch (e) { console.warn("Failed to load series history:", e.message); }
    finally { setSeriesLoading(false); }
  };

  useEffect(() => { setLoading(true); setNotFound(false); fetchMeeting(); fetchSeriesHistory(); }, [mid]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API}/hirality-meetings/languages`, { headers: authHeader() })
      .then((r) => setLanguages(r.data || []))
      .catch((e) => console.warn("Failed to load languages:", e.message));
  }, []);

  // Prep briefing — only useful before the call happens, so only fetched
  // for a Scheduled meeting, once it's loaded.
  useEffect(() => {
    if (meeting?.status !== "Scheduled") return;
    setBriefingLoading(true);
    axios.get(`${API}/hirality-meetings/${meeting.id}/briefing`, { headers: authHeader() })
      .then((r) => setBriefing(r.data))
      .catch((e) => console.warn("Failed to load briefing:", e.message))
      .finally(() => setBriefingLoading(false));
  }, [meeting?.id, meeting?.status]);

  const translateSummary = async (lang) => {
    if (!meeting) return;
    if (lang === "en") { setSummaryLang("en"); return; }
    if (meeting.translated_summaries?.[lang]) { setSummaryLang(lang); return; }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/hirality-meetings/${meeting.id}/translate-summary`, { language: lang }, { headers: authHeader() });
      setMeeting((v) => ({ ...v, translated_summaries: { ...(v.translated_summaries || {}), [lang]: res.data } }));
      setSummaryLang(lang);
    } catch (e) { console.error("Translate failed:", e); }
    finally { setTranslating(false); }
  };

  const startMeeting = async (m) => {
    try { await axios.post(`${API}/hirality-meetings/${m.id}/start`, {}, { headers: authHeader() }); }
    catch (e) { console.warn("start ping failed:", e.message); }
    window.open(buildMeetingLink(m), "_blank", "noopener,noreferrer");
  };

  const copyLink = (m) => {
    navigator.clipboard.writeText(buildMeetingLink(m)).then(() => {
      setCopiedId("link");
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const copyDraft = (key, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedDraft(key);
      setTimeout(() => setCopiedDraft(null), 2000);
    });
  };

  const confirmCancel = async () => {
    try {
      await axios.put(`${API}/hirality-meetings/${mid}`, { status: "Cancelled" }, { headers: authHeader() });
      setCancelOpen(false);
      fetchMeeting();
    } catch (e) { console.error("Cancel failed:", e); }
  };

  if (loading) {
    return (
      <div className={styles.pageRoot}>
        <div className={styles.loader}><div className={styles.spinner} />Loading…</div>
      </div>
    );
  }

  if (notFound || !meeting) {
    return (
      <div className={styles.pageRoot}>
        <button className={styles.btnSecondary} onClick={() => navigate("/admin/hirality-meetings")}>
          <ArrowLeft size={13} /> Back to Meetings
        </button>
        <div style={{ marginTop: 24, color: T.muted, fontSize: 13 }}>This meeting couldn't be found.</div>
      </div>
    );
  }

  const activeSummary = summaryLang === "en"
    ? { summary: meeting.summary, key_topics: meeting.key_topics || [], action_items: meeting.action_items || [] }
    : (meeting.translated_summaries?.[summaryLang] || { summary: meeting.summary, key_topics: meeting.key_topics || [], action_items: meeting.action_items || [] });

  const isScheduled = meeting.status === "Scheduled";
  const hasAIContent = meeting.summary || meeting.chapters?.length || meeting.engagement_heatmap || meeting.followup_drafts;

  return (
    <div className={styles.pageRoot}>
      <button className={styles.btnSecondary} onClick={() => navigate("/admin/hirality-meetings")} style={{ marginBottom: 18 }}>
        <ArrowLeft size={13} /> Back to Meetings
      </button>

      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div className={styles.badge} style={{ marginBottom: 0 }}>HIRALITY MEETINGS</div>
            <span className={`${styles.badge} ${styles[STATUS_BADGE[meeting.status] || "grey"]}`}>{meeting.status}</span>
            {(() => {
              const mt = getMeetingType(meeting.meeting_type || "meeting");
              const c = T[mt.color];
              const Icon = mt.icon;
              return (
                <span className={styles.badge} style={{ background: `${c}18`, color: c }}>
                  <Icon size={10} style={{ marginRight: 2 }} /> {mt.label}
                </span>
              );
            })()}
            {meeting.series_id && (
              <span className={`${styles.badge} ${styles.purple}`}>
                <Repeat size={10} style={{ marginRight: 2 }} /> Recurring · {meeting.recurrence_frequency}
                {meeting.occurrence_index ? ` · #${meeting.occurrence_index}` : ""}
              </span>
            )}
          </div>
          <h2>{meeting.title}</h2>
          {meeting.description && <p style={{ maxWidth: 640, lineHeight: 1.6 }}>{meeting.description}</p>}
        </div>
        {isScheduled && (
          <div className={styles.headerRight}>
            <button className={styles.btnSm} onClick={() => copyLink(meeting)}>
              {copiedId === "link" ? <Check size={12} /> : <Copy size={12} />} {copiedId === "link" ? "Copied" : "Copy Link"}
            </button>
            <button className={styles.btnSm} onClick={() => setEditOpen(true)}><Edit2 size={12} /> Edit</button>
            <button className={styles.btnSm} style={{ color: T.red, borderColor: `${T.red}40` }} onClick={() => setCancelOpen(true)}>
              <XCircle size={12} /> Cancel
            </button>
            <button className={styles.btnPrimary} onClick={() => startMeeting(meeting)}><Video size={14} /> Start Now</button>
          </div>
        )}
      </div>

      {/* ── Stat row ── */}
      <div className={styles.card} style={{ marginBottom: 18 }}>
        <div className={styles.cardBody} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 18 }}>
          <Stat T={T} icon={Calendar} label="Date" value={meeting.meeting_date} />
          <Stat T={T} icon={Clock} label="Time" value={`${meeting.meeting_time} · ${meeting.duration_minutes} min`} />
          <Stat T={T} icon={User} label="Host" value={meeting.host_name || "—"} />
          <Stat T={T} icon={Users2} label="Participants" value={meeting.participants?.length || 0} />
          <Stat T={T} icon={Hash} label="Room" value={meeting.room_name} />
        </div>
      </div>

      {/* ── Participants ── */}
      <SectionCard T={T} icon={Users2} iconColor={T.cyan} title="Participants"
        subtitle="Everyone invited to this meeting, host included.">
        {meeting.participants?.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {meeting.participants.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 20, padding: "5px 12px 5px 5px" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: AVATAR_COLORS[i % AVATAR_COLORS.length], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff" }}>
                  {(p.name || p.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{p.name || p.email}</div>
                  {p.name && p.email && <div style={{ fontSize: 10, color: T.muted }}>{p.email}</div>}
                </div>
              </div>
            ))}
          </div>
        ) : <span style={{ fontSize: 12.5, color: T.muted }}>No participants recorded</span>}
      </SectionCard>

      {/* ── Prep Briefing (Scheduled meetings only) ── */}
      {isScheduled && (
        <SectionCard T={T} icon={Sparkles} iconColor={T.cyan} title="Prep Briefing"
          subtitle="What you need to know before this starts — from the description and any prior sessions.">
          {briefingLoading ? (
            <div style={{ fontSize: 12.5, color: T.muted }}>Generating briefing…</div>
          ) : briefing?.briefing ? (
            <>
              <p style={{ fontSize: 13.5, color: T.text, margin: 0, lineHeight: 1.7 }}>{briefing.briefing}</p>
              {briefing.watch_for?.length > 0 && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  {briefing.watch_for.map((w, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.amber, marginTop: 7, flexShrink: 0 }} />
                      <span style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.6 }}>{w}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <span style={{ fontSize: 12.5, color: T.muted }}>Not enough context yet — add a description, or this will fill in once earlier sessions exist.</span>
          )}
        </SectionCard>
      )}

      {/* ── Previous Sessions (recurring meetings only) ── */}
      {seriesHistory.is_recurring && (
        <SectionCard T={T} icon={History} iconColor={T.purple} title="Previous Sessions"
          subtitle="Date-wise history of earlier occurrences of this recurring meeting — for a quick catch-up before this one starts.">
          {seriesLoading ? (
            <div style={{ fontSize: 12.5, color: T.muted }}>Loading history…</div>
          ) : seriesHistory.occurrences.length === 0 ? (
            <div style={{ fontSize: 12.5, color: T.muted }}>No earlier sessions yet — this is the first one.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {seriesHistory.occurrences.map((o) => {
                const isOpen = expandedOccurrence === o.id;
                return (
                  <div key={o.id} style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                    <button onClick={() => setExpandedOccurrence(isOpen ? null : o.id)}
                      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{o.meeting_date}</span>
                        <span className={`${styles.badge} ${styles[STATUS_BADGE[o.status] || "grey"]}`}>{o.status}</span>
                        {o.action_items_count > 0 && (
                          <span style={{ fontSize: 10.5, color: T.muted }}>{o.action_items_count} action item{o.action_items_count > 1 ? "s" : ""}</span>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span onClick={(e) => { e.stopPropagation(); navigate(`/admin/hirality-meetings/${o.id}`); }}
                          style={{ fontSize: 11, color: T.cyan, fontWeight: 600 }}>Open →</span>
                        {isOpen ? <ChevronUp size={13} color={T.muted} /> : <ChevronDown size={13} color={T.muted} />}
                      </div>
                    </button>
                    {isOpen && (
                      <div style={{ padding: "0 14px 12px" }}>
                        {o.summary ? (
                          <p style={{ fontSize: 12.5, color: T.muted, margin: "0 0 8px", lineHeight: 1.6 }}>{o.summary}</p>
                        ) : (
                          <p style={{ fontSize: 12, color: T.dim, margin: "0 0 8px" }}>No summary — this session may not have a captured transcript.</p>
                        )}
                        {o.key_topics?.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {o.key_topics.map((t, i) => (
                              <span key={i} style={{ fontSize: 10.5, padding: "3px 9px", borderRadius: 20, background: `${T.purple}18`, color: T.purple, fontWeight: 600 }}>{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      )}

      {!hasAIContent && !isScheduled && (
        <div className={styles.card} style={{ marginBottom: 18 }}>
          <div className={styles.cardBody} style={{ display: "flex", alignItems: "center", gap: 10, color: T.muted, fontSize: 13 }}>
            <Info size={15} /> No transcript was captured for this call, so no AI insights are available.
          </div>
        </div>
      )}

      {isScheduled && (
        <div className={styles.card} style={{ marginBottom: 18 }}>
          <div className={styles.cardBody} style={{ display: "flex", alignItems: "center", gap: 10, color: T.muted, fontSize: 13 }}>
            <Info size={15} /> AI summary, chapters, engagement, and follow-up drafts will appear here automatically once this meeting ends.
          </div>
        </div>
      )}

      {/* ── AI Summary ── */}
      {meeting.summary && (
        <SectionCard T={T} icon={FileText} iconColor={T.cyan} title="AI Summary"
          subtitle={SUMMARY_SUBTITLE[meeting.meeting_type || "meeting"]}
          headerRight={languages.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {translating && <span style={{ fontSize: 11, color: T.muted }}>Translating…</span>}
              <Languages size={12} style={{ color: T.muted }} />
              <select value={summaryLang} onChange={(e) => translateSummary(e.target.value)} disabled={translating}
                style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text,
                  fontSize: 11.5, fontFamily: "Outfit,sans-serif", padding: "5px 8px", cursor: "pointer" }}>
                <option value="en">English (Original)</option>
                {languages.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
              </select>
            </div>
          )}>
          <p style={{ fontSize: 14, color: T.text, margin: 0, lineHeight: 1.7 }}>{activeSummary.summary}</p>
          {activeSummary.key_topics?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 14 }}>
              {activeSummary.key_topics.map((t, i) => (
                <span key={i} style={{ fontSize: 11.5, padding: "4px 11px", borderRadius: 20, background: `${T.purple}18`, color: T.purple, fontWeight: 600 }}>{t}</span>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* ── Action Items ── */}
      {activeSummary.action_items?.length > 0 && (
        <SectionCard T={T} icon={ListChecks} iconColor={T.green} title="Action Items"
          subtitle="Real commitments made during the call, with the owner and any deadline mentioned.">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
            {activeSummary.action_items.map((ai, i) => (
              <div key={i} style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 13, color: T.text, fontWeight: 600, lineHeight: 1.5 }}>{ai.task}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 20, background: `${T.cyan}18`, color: T.cyan, fontWeight: 600 }}>{ai.owner}</span>
                  <span style={{ fontSize: 10.5, padding: "2px 8px", borderRadius: 20, background: `${T.amber}18`, color: T.amber, fontWeight: 600 }}>{ai.due_hint}</span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Action Item Follow-Up (recurring meetings only, from the 2nd occurrence on) ── */}
      {meeting.carried_over_action_items?.length > 0 && (
        <SectionCard T={T} icon={CheckCircle2} iconColor={T.green} title="Action Item Follow-Up"
          subtitle="Last session's action items, checked against this call's transcript.">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {meeting.carried_over_action_items.map((ai, i) => {
              const meta = {
                completed:     { icon: CheckCircle2,   color: T.green, label: "Completed" },
                in_progress:   { icon: CircleDashed,   color: T.amber, label: "In Progress" },
                not_mentioned: { icon: AlertCircle,    color: T.muted, label: "Not Mentioned" },
              }[ai.status] || { icon: AlertCircle, color: T.muted, label: ai.status };
              const StatusIcon = meta.icon;
              return (
                <div key={i} style={{ display: "flex", gap: 10, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 14px" }}>
                  <StatusIcon size={15} style={{ color: meta.color, flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 12.5, color: T.text, fontWeight: 600 }}>{ai.task}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{ai.owner}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: meta.color }}>{meta.label}</span>
                      {ai.evidence && <span style={{ fontSize: 11.5, color: T.dim, fontStyle: "italic" }}>“{ai.evidence}”</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* ── Chapters ── */}
      {meeting.chapters?.length > 0 && (
        <SectionCard T={T} icon={FileText} iconColor={T.cyan} title="Chapters"
          subtitle="Topic-boundary index over the transcript — no video is recorded, so “jump to” scrolls straight to that point below instead of seeking a player.">
          <MeetingChapters meetingId={meeting.id} chapters={meeting.chapters} transcript={meeting.transcript} T={T} initialChapterIndex={deepLinkChapter} />
        </SectionCard>
      )}

      {/* ── Engagement Heatmap ── */}
      {meeting.engagement_heatmap && (
        <SectionCard T={T} icon={FileText} iconColor={T.amber} title="Engagement Heatmap"
          subtitle="Pacing, tone, and energy over the course of the call, plus who talked the most.">
          <EngagementHeatmap heatmap={meeting.engagement_heatmap} T={T} avatarColors={AVATAR_COLORS} />
        </SectionCard>
      )}

      {/* ── Follow-Up Drafts ── */}
      {meeting.followup_drafts && (
        <SectionCard T={T} icon={Mail} iconColor={T.purple} title="Follow-Up Drafts"
          subtitle="Ready-to-send messages drafted from what was actually said — review and edit before sending, nothing here is sent automatically.">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
            {[
              { key: "client", icon: Mail, label: "Client Recap Email", data: meeting.followup_drafts.client_email,
                text: meeting.followup_drafts.client_email ? `Subject: ${meeting.followup_drafts.client_email.subject}\n\n${meeting.followup_drafts.client_email.body}` : "" },
              { key: "team", icon: ListChecks, label: "Team Checklist Email", data: meeting.followup_drafts.team_checklist_email,
                text: meeting.followup_drafts.team_checklist_email ? `Subject: ${meeting.followup_drafts.team_checklist_email.subject}\n\n${meeting.followup_drafts.team_checklist_email.body}` : "" },
              { key: "chat", icon: MessageCircle, label: "Slack / Teams Message", data: meeting.followup_drafts.chat_summary,
                text: meeting.followup_drafts.chat_summary || "" },
            ].filter((d) => d.data).map(({ key, icon: Icon, label, text }) => (
              <div key={key} style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 13px", borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    <Icon size={12} /> {label}
                  </span>
                  <button className={styles.btnSm} onClick={() => copyDraft(key, text)}>
                    {copiedDraft === key ? <Check size={11} /> : <Copy size={11} />}
                    {copiedDraft === key ? "Copied" : "Copy"}
                  </button>
                </div>
                <div style={{ padding: "12px 13px", fontSize: 12.5, color: T.text, lineHeight: 1.7, whiteSpace: "pre-wrap", maxHeight: 260, overflowY: "auto", flex: 1 }}>
                  {text}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Edit modal (Scheduled only) ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <div style={{ background: T.card, border: `1px solid ${T.cyan}33`, borderRadius: 14, width: 520, fontFamily: "Outfit,sans-serif", overflow: "hidden" }}>
          <div style={{ padding: "18px 24px", borderBottom: `1px solid ${T.border}`, background: `${T.cyan}0A`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: T.text }}>Edit Meeting</h3>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: T.muted }} onClick={() => setEditOpen(false)}><X size={16} /></button>
          </div>
          <div style={{ padding: 24 }}>
            <MeetingForm meetingData={meeting} header="Edit" onCloseDialog={() => setEditOpen(false)}
              onSaved={() => { setEditOpen(false); fetchMeeting(); }} />
          </div>
        </div>
      </Dialog>

      <Popup
        open={cancelOpen}
        title="Cancel Meeting"
        message="Cancel this meeting? Participants will need to be notified separately."
        handleClose={() => setCancelOpen(false)}
        handleConfirm={confirmCancel}
        type="alert"
      />
    </div>
  );
}
